/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/navigation';
import { useLanguage } from '@/providers/LanguageProvider';
import { CONVERSATION_COUNT_IN_ONE_TAB } from '@/utils/constants';
import { getTemplateLabelEnums } from '@/utils/evalTemplates';
import {
  extractData,
  groupConversationsByTraceId,
} from '@/utils/promptSequenceViewDataVariation';
import { sendEvalPromptToKahu } from '@/utils/sendEvalPromptToKahu';
import EvalBadge from '@/ux-common/src/components/evals/eval-badge';
import Icons from '@/ux-common/src/icons/icons';
import { EvalResponseType, EvalResults } from '@/ux-common/src/types/api';
import { sizeStyles } from '@/ux-common/src/utils/constants';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import { Separator } from '@radix-ui/react-separator';
import {
  ItemsToRender,
  TSequenceData,
  TSequenceDataForConversations,
  TSequenceDataGlobalMatchOffset,
} from 'types/prompt-sequence-view';

import { cn } from '@/lib/utils';
import { useConcurrentSearch } from '@/hooks/useConcurrentSearch';
import { useEvals } from '@/hooks/useEvals';
import { useSplitPromptSequence } from '@/hooks/useSplitPromptSequence';

import SearchWithNavigation from '../../ux-common/src/components/common/search-with-navigation';
import { EnhancedMultiSelect } from '../combobox';
import { CustomButton } from '../common';
import { FACT_NAME_LABEL_MAP } from '../display-page/fact-name-label-map';
import {
  copyTraceId,
  truncateTraceId,
} from '../display-page/traces/traceUtils';
import { Icons as PortalIcons, System } from '../icons';
import { PromptSequenceIcons } from '../icons/icons';
import { NotFoundMessage } from '../not-found';
import BasicLoader from '../spinner/basic-loader';
import Loader from '../spinner/loader';
import { ScrollArea } from '../ui/scroll-area';
import { Skeleton } from '../ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { DetailSequenceView } from './detail-sequence-view';
import SequenceSeparator from './sequence-separator';
import { SplitPanel } from './split-panel';

interface PromptSequenceViewProps {
  data: Array<{ trace_id: string }>;
  currentPageIndex: number; // zero‑based
  loading?: boolean;
  onSplit?: () => void;
  onCloseSplit?: () => void;
  factName: string;
  spanEvals: EvalResponseType | null;
  conversationEvals: EvalResponseType | null;
  traceEvals: EvalResponseType | null;
  // New props to control EvalModal
  setEvaluateOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleIdSpecificEvalChange: (
    id: string,
    source: 'trace' | 'conversation'
  ) => void;
  appName?: string;
  workflowName?: string;
  filterStartTime?: string | null;
  filterEndTime?: string | null;
}

export const PromptSequenceView: FC<PromptSequenceViewProps> = ({
  data,
  currentPageIndex,
  loading,
  onSplit,
  onCloseSplit,
  factName,
  spanEvals,
  traceEvals,
  conversationEvals,
  setEvaluateOpen,
  handleIdSpecificEvalChange,
  appName,
  workflowName,
  filterStartTime,
  filterEndTime,
}) => {
  // Pass the data, to extract in the form of input and output.
  const promptsData = useMemo(() => extractData(data), [data]);
  const { state, evaluateActions } = useEvals();
  const { evalTemplates } = state;
  const { traceEvalLoading, convoEvalLoading, spanEvalLoading } = state;

  const scrollAreaRef = useRef<HTMLDivElement | null>(null);

  const { splitPayload, setSplitPayload } = useSplitPromptSequence();

  const [maxTracesToShow, setMaxTracesToShow] = useState<number>(
    CONVERSATION_COUNT_IN_ONE_TAB
  );
  const [selectedTraceIds, setSelectedTraceIds] = useState<string[]>([]);

  const [showMoreInProgress, setShowMoreInProgress] = useState<boolean>(false);
  const { messages } = useLanguage();
  const langData = messages;
  const router = useRouter();

  const promptSequenceConstants = messages?.PromptSequenceView;

  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());

  const current = promptsData[currentPageIndex];

  const {
    searchInput,
    deferredSearchTerm,
    handleInputChange,
    handleClear,
    navigableMatches,
    currentMatchIndex,
    onPreviousMatch,
    onNextMatch,
  } = useConcurrentSearch(
    promptsData[currentPageIndex]?.sequenceData,
    traceEvals,
    selectedTraceIds
  );

  const tracesNavigationOptions =
    factName === 'traces'
      ? [
          {
            key: 'copyTraceId',
            label: langData?.PromptSequenceView.General.copyTraceId,
            icon: PortalIcons.Copy('h-4 w-4 text-[#257C9D]'),
          },
          {
            key: 'searchByTraceId',
            label: langData?.PromptSequenceView.General.searchTraceById,
            icon: Icons.Search('h-4 w-4 stroke-[#257C9D]'),
          },
          {
            key: 'evaluateTrace',
            label: langData?.PromptSequenceView.General.evaluateTrace,
            icon: Icons.EvaluateIcon('h-4 w-4 text-[#257C9D]'),
          },
        ]
      : factName === 'conversations'
        ? [
            {
              key: 'copyConversationId',
              label: langData?.PromptSequenceView.General.copyConversationId,
              icon: PortalIcons.Copy('h-4 w-4 text-[#257C9D]'),
            },
            {
              key: 'evaluateConversation',
              label: langData?.PromptSequenceView.General.evaluateConversation,
              icon: Icons.EvaluateIcon('h-4 w-4 text-[#257C9D]'),
            },
          ]
        : [];

  const currentPagePromptsData = useMemo(() => {
    if (!current) return null;

    return factName !== 'traces'
      ? groupConversationsByTraceId(current)
      : current;
  }, [current, factName, promptSequenceConstants]);

  // Filter traces based on trace id dropdown selection
  const filterTraces = useCallback(
    (traces: any[] | undefined): any[] => {
      if (!traces) return [];
      if (selectedTraceIds.length === 0) {
        return traces;
      }
      return traces.filter((item) => selectedTraceIds.includes(item.trace_id));
    },
    [selectedTraceIds]
  );

  // Get a unique set of all span_ids that have matches
  const matchingSpanIds = useMemo(() => {
    if (navigableMatches.length === 0) return new Set();
    return new Set(navigableMatches.map((match) => match.span_id));
  }, [navigableMatches]);

  // Prepare the final list of items that will be shown on screen
  const itemsToRender = useMemo(() => {
    const allItems = currentPagePromptsData?.sequenceData || [];

    if (!allItems.length) return [];

    let runningOffset = 0;

    const itemsWithOffsets = allItems.map((item) => {
      // Case A: single span-level item
      if ('span_id' in item) {
        const matchesInThisItem = navigableMatches.filter(
          (m) => m.span_id === item.span_id
        ).length;

        const withOffset = {
          ...item,
          globalMatchOffset: runningOffset, // start offset
        };

        runningOffset += matchesInThisItem;
        return withOffset;
      }

      // Case B: conversation group, assign offsets to each span inside
      if ('spans_data' in item) {
        const spansWithOffsets = item.spans_data.map((span) => {
          const matchesInSpan = navigableMatches.filter(
            (m) => m.span_id === span.span_id
          ).length;

          const spanWithOffset = { ...span, globalMatchOffset: runningOffset };
          runningOffset += matchesInSpan;
          return spanWithOffset;
        });

        return { ...item, spans_data: spansWithOffsets };
      }

      // Fallback
      return item;
    });

    // Apply trace filter
    const traceFilteredItems =
      selectedTraceIds.length === 0
        ? itemsWithOffsets
        : itemsWithOffsets.filter((item) =>
            selectedTraceIds.includes(item.trace_id)
          );

    // Apply search filter
    const finalItems = deferredSearchTerm.trim()
      ? traceFilteredItems.filter((item) => {
          if ('span_id' in item) {
            return matchingSpanIds.has(item.span_id);
          }
          if ('spans_data' in item) {
            return item.spans_data.some((span: any) =>
              matchingSpanIds.has(span.span_id)
            );
          }
          return false;
        })
      : traceFilteredItems;

    return finalItems;
  }, [
    currentPagePromptsData?.sequenceData,
    navigableMatches,
    deferredSearchTerm,
    matchingSpanIds,
    selectedTraceIds,
    searchInput,
  ]);

  // Auto-collapse/expand spans based on search results
  const stableMatchingSpanIds = useMemo(
    () => matchingSpanIds,
    [JSON.stringify(Array.from(matchingSpanIds))]
  );

  const stablePageData = useMemo(
    () => currentPagePromptsData,
    [JSON.stringify(currentPagePromptsData)]
  );

  useEffect(() => {
    if (deferredSearchTerm.trim()) {
      // Get a list of ALL possible span IDs
      const allSpanIds = new Set(
        currentPagePromptsData?.sequenceData
          .map((item) => ('span_id' in item ? item.span_id : null))
          .filter((id): id is string => id !== null)
      );

      // Find the spans that are NOT on our matchingSpanIds list
      const spansToCollapse = new Set(
        Array.from(allSpanIds).filter(
          (id) => !matchingSpanIds.has(id as string)
        )
      );

      // Collapse spans without matches
      setCollapsedIds(spansToCollapse);
    } else {
      // If the search bar is empty, expand all spans
      setCollapsedIds(new Set());
    }
  }, [deferredSearchTerm, stableMatchingSpanIds, stablePageData]);

  // On click on next/prev button (pagination) -- Show the default prompt sequence view.
  // TIP: Reset the search term, split panel.
  useEffect(() => {
    setSplitPayload(null);
  }, [currentPageIndex, setSplitPayload]);

  // Reset maxTracesToShow when the main data prop changes
  useEffect(() => {
    setMaxTracesToShow(CONVERSATION_COUNT_IN_ONE_TAB);
  }, [data, currentPageIndex]);

  function handleShowMore() {
    setShowMoreInProgress(true);
    setTimeout(() => {
      const remainingItems =
        promptsData[currentPageIndex]?.sequenceData.length - maxTracesToShow;
      setMaxTracesToShow(
        (prev) => prev + Math.min(remainingItems, CONVERSATION_COUNT_IN_ONE_TAB)
      );
      setShowMoreInProgress(false);
    }, 1000);
  }

  // Rest and select all trace IDs when page changes
  useEffect(() => {
    const traceIds =
      promptsData[currentPageIndex]?.sequenceData?.map(
        (item) => item.trace_id
      ) || [];

    setSelectedTraceIds(traceIds);
  }, [currentPageIndex]);

  const factLabels = FACT_NAME_LABEL_MAP[factName] || {
    label: factName,
    idLabel: `${factName} ID`,
  };

  // If loading is true, show a basic loader.
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader
          title={messages?.PromptsListing.getting_prompts}
          description={messages?.General.please_wait}
        />
      </div>
    );
  }

  const currentPromptId = promptsData[currentPageIndex]?.prompt_id
    ? truncateTraceId(promptsData[currentPageIndex]?.prompt_id)
    : '';

  // If the application does not have prompts, show not found message.
  if (promptsData.length === 0) {
    return (
      <NotFoundMessage
        title={promptSequenceConstants?.NotFound?.no_prompt_found_title}
        message={promptSequenceConstants?.NotFound?.no_prompt_found_description}
      />
    );
  }

  // If the current page doesn't have input prompts, show not found message.
  if (
    !current ||
    current.sequenceData.every((item) => item.inputData.length === 0)
  ) {
    return (
      <NotFoundMessage
        title={promptSequenceConstants?.NotFound?.no_prompt_found_title}
        message={promptSequenceConstants?.NotFound?.no_prompt_found_description}
      />
    );
  }

  const matchConversationEvalArray =
    conversationEvals &&
    currentPagePromptsData &&
    conversationEvals.results
      .filter(
        (convEval) => convEval.fact_id === currentPagePromptsData.prompt_id
      )
      .filter((convEval) => convEval.eval_found === true);

  const matchTraceEvalArray =
    traceEvals &&
    currentPagePromptsData &&
    traceEvals.results
      .filter(
        (traceEval) =>
          traceEval.fact_id === currentPagePromptsData.sequenceData[0].trace_id
      )
      .filter((traceEval) => traceEval.eval_found === true);

  const sequenceEvalArr = (item: any) => {
    return (
      spanEvals &&
      spanEvals.results
        .filter(
          (e: EvalResults) => e.fact_id === `${item.trace_id}.${item.span_id}`
        )
        .filter((spanEval) => spanEval.eval_found === true)
    );
  };

  /**
   * If traces then match to the traceEvalArray
   * Otherwise we match with the matchConversationEvalArray for fact_name other than traces and inferences
   */
  const evalArray =
    factName === 'traces' ? matchTraceEvalArray : matchConversationEvalArray;

  // Toggle prompt on click
  const toggleCollapse = (id: string) => {
    setCollapsedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id); // expand
      } else {
        newSet.add(id); // collapse
      }
      return newSet;
    });
  };

  // Toggle all prompts on click
  const promptCollapseHandler = () => {
    setCollapsedIds((prev) => {
      // collect all span ids
      const allSpanIds =
        currentPagePromptsData?.sequenceData?.map((item) =>
          'span_id' in item ? item.span_id : item.trace_id
        ) || [];

      const allCollapsed = allSpanIds.every((id) => prev.has(id));

      if (allCollapsed) {
        // Expand all
        return new Set();
      } else {
        // Collapse all
        return new Set(allSpanIds);
      }
    });
  };

  const handleClick = (key: string) => {
    if (!currentPagePromptsData) return;
    switch (key) {
      case 'searchByTraceId':
        router.push(
          `traces?trace_id=${currentPagePromptsData?.prompt_id}&activeTab=search&factName=traces`
        );
        break;
      case 'copyTraceId':
      case 'copyConversationId':
        copyTraceId(
          currentPagePromptsData?.prompt_id,
          promptSequenceConstants?.General?.traceIdCopied
        );
        break;
      case 'evaluateTrace':
      case 'evaluateConversation':
        setEvaluateOpen(true);
        handleIdSpecificEvalChange(
          currentPagePromptsData?.prompt_id,
          key === 'evaluateTrace' ? 'trace' : 'conversation'
        );
        break;
      default:
        break;
    }
  };

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-h-0 w-full flex-col">
        <div className="flex items-start border-b border-[#0A343C] px-3 py-2">
          <div className="flex h-full items-start gap-1 text-form-input-text font-normal text-[#8C9EA1]">
            <div className="flex h-[40px] items-center justify-center">
              <span className="text-nowrap">{`${factLabels.idLabel}: `}</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {factName === 'traces' || factName === 'conversations' ? (
                      <DropdownMenu modal={false}>
                        <TooltipProvider>
                          <Tooltip>
                            <DropdownMenuTrigger asChild>
                              <TooltipTrigger asChild>
                                <span className="cursor-pointer hover:text-body-secondary">
                                  {currentPromptId}
                                </span>
                              </TooltipTrigger>
                            </DropdownMenuTrigger>
                            <TooltipContent className="rounded p-[9px] text-[11px] leading-4 tracking-[2%] text-white">
                              <p>{factLabels.idLabel}</p>
                              <span>
                                {currentPagePromptsData?.prompt_id ?? ''}
                              </span>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <DropdownMenuContent
                          align="end"
                          className="z-10 min-w-44 cursor-pointer rounded-none bg-white p-0 text-h5 text-black"
                        >
                          {tracesNavigationOptions.map((option) => (
                            <DropdownMenuItem
                              key={option.key}
                              className={cn(
                                'flex cursor-pointer items-center gap-2 p-2 hover:bg-gray-100',
                                option.key === 'evaluateTrace' &&
                                  'border-t border-[#C1C9CF]',
                                option.key === 'evaluateConversation' &&
                                  'border-t border-[#C1C9CF]'
                              )}
                              onClick={() => handleClick(option.key)}
                            >
                              {option.icon && (
                                <span className="flex-shrink-0">
                                  {option.icon}
                                </span>
                              )}
                              <span>{option.label}</span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <span className="cursor-default text-link-active">
                        {currentPromptId}
                      </span>
                    )}
                  </TooltipTrigger>
                  <TooltipContent className="rounded p-[9px] text-[11px] leading-4 tracking-[2%] text-white">
                    <p>{factLabels.idLabel}</p>
                    <span>{currentPagePromptsData?.prompt_id ?? ''}</span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {factName !== 'traces' && factName !== 'conversations' && (
                <button
                  onClick={() =>
                    copyTraceId(
                      currentPagePromptsData?.prompt_id || '',
                      `${factLabels.idLabel} ${langData?.General?.copied_to_clipboard}`
                    )
                  }
                  className="ml-1 cursor-pointer text-gray-400 hover:text-white"
                >
                  {PortalIcons.Copy('h-4 w-4')}
                </button>
              )}
            </div>
            {/*
             * Combined responsibility to show eval status for
             * traces and custom factName including conversations
             */}
            {convoEvalLoading || traceEvalLoading ? (
              <>
                <Separator
                  orientation="vertical"
                  className="my-auto ml-4 mr-2 h-[90%] w-[1px] bg-line-tertiary"
                />
                <Skeleton
                  className={cn(
                    'animate-pulse-custom my-auto w-16 rounded-full bg-[#8C9EA1]',
                    sizeStyles['default']
                  )}
                />
              </>
            ) : (
              evalArray &&
              evalArray.length > 0 && (
                <>
                  <Separator
                    orientation="vertical"
                    className="my-auto ml-4 mr-2 h-[90%] w-[1px] bg-line-tertiary"
                  />
                  <div className="flex max-h-full min-h-[40px] flex-wrap items-center gap-1">
                    {evalArray.map((evaluation) => (
                      <EvalBadge
                        key={`${evaluation.fact_id}_${evaluation.eval_timestamp}`}
                        evalObj={evaluation}
                        submitManualLabel={evaluateActions?.submitManualLabel}
                        createEvalsJobs={evaluateActions?.createEvalsJobs}
                        factName={factName}
                        appName={
                          appName ??
                          traceEvals?.app_name ??
                          conversationEvals?.app_name ??
                          ''
                        }
                        workflowName={evaluation.workflow_name ?? workflowName}
                        templateEnums={getTemplateLabelEnums(
                          evalTemplates,
                          evaluation.eval_name
                        )}
                        templateLoading={state.templateLoading}
                        sendEvalPromptToKahu={sendEvalPromptToKahu}
                        filterStartTime={filterStartTime}
                        filterEndTime={filterEndTime}
                      />
                    ))}
                  </div>
                </>
              )
            )}
          </div>

          {factName !== 'traces' && (
            <div className="flex flex-1 items-center justify-between">
              <div className="flex items-center gap-2">
                <Separator
                  orientation="vertical"
                  className="ml-4 mr-2 h-8 w-[1px] bg-line-tertiary"
                />
                <span className="text-kpi-label text-form-input-disabled">
                  {messages?.General?.traces}:
                </span>
                <EnhancedMultiSelect
                  key={`page-${currentPageIndex}`}
                  options={
                    currentPagePromptsData?.sequenceData?.map(
                      (item, index) => ({
                        key: item.trace_id,
                        id: index,
                        value: item.trace_id,
                        label: truncateTraceId(item.trace_id),
                      })
                    ) || []
                  }
                  onValueChange={(selectedOptionsValues: string[]) => {
                    setSelectedTraceIds(selectedOptionsValues);
                  }}
                  value={selectedTraceIds}
                  className="w-fit border-none p-0"
                  placeholder="All"
                  variant="inverted"
                  allCheckbox={true}
                />
              </div>
            </div>
          )}
        </div>
        {/* TIP: Remove the factName condition check, if you want to use the below toolbar inside all the facts. */}
        {factName === 'traces' && (
          <div className="flex h-12 items-center border-b border-[#0A343C] px-3 py-2">
            <span
              className="ml-2 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded bg-transparent p-0 text-white hover:bg-[#257C9D1F] focus:bg-[#257C9D1F] active:bg-[#257C9D29] disabled:bg-transparent"
              onClick={promptCollapseHandler}
            >
              {collapsedIds.size === 0
                ? PromptSequenceIcons.ExpandAllIcon('h-6 w-6')
                : PromptSequenceIcons.CollapseAllIcon('h-6 w-6')}
            </span>

            <Separator
              className="mx-3 h-full min-h-6 border border-line-tertiary"
              orientation="vertical"
            />
            {/* Span collapsed/expanded search functionality */}
            <SearchWithNavigation
              value={searchInput}
              onChange={handleInputChange}
              onClear={handleClear}
              className=""
              inputClassName=""
              matches={navigableMatches}
              currentIndex={currentMatchIndex}
              onPreviousMatch={onPreviousMatch}
              onNextMatch={onNextMatch}
              disabled={false}
            />
          </div>
        )}

        <ScrollArea id="prompt-sequence-scroll-area" ref={scrollAreaRef}>
          {itemsToRender.map(
            (item: ItemsToRender, index) =>
              index < maxTracesToShow && (
                <React.Fragment key={`${item.trace_id}-${index}`}>
                  {/*factName !== 'traces' -> matches all the custom factNames including conversation  */}
                  {factName !== 'traces' ? (
                    <SequenceSeparator
                      key={`${item.trace_id}-separator`}
                      id={item.trace_id}
                      label={promptSequenceConstants?.General?.traceId || ''}
                      showDropdown={factName === 'conversations' && true}
                      traceEvalArr={matchTraceEvalArray ?? undefined}
                      factName={factName}
                      appName={appName ?? traceEvals?.app_name ?? ''}
                      workflowName={workflowName}
                      startTime={
                        'spans_data' in item
                          ? item.spans_data?.[0]?.inputData?.[0]?.startTime
                          : undefined
                      }
                      endTime={
                        'spans_data' in item
                          ? item.spans_data?.[item.spans_data.length - 1]
                              ?.endTime
                          : undefined
                      }
                      badgeFactName="traces"
                      setEvaluateOpen={setEvaluateOpen}
                      handleIdSpecificEvalChange={handleIdSpecificEvalChange}
                      filterStartTime={filterStartTime}
                      filterEndTime={filterEndTime}
                    />
                  ) : (
                    promptSequenceConstants?.General?.traceId &&
                    'span_id' in item && (
                      <div className="flex items-center justify-center py-2">
                        <SequenceSeparator
                          id={item.span_id}
                          traceId={item.trace_id}
                          label={promptSequenceConstants?.General?.spanId}
                          showDropdown={false}
                          subtype={item.subtype}
                          setEvaluateOpen={setEvaluateOpen}
                          handleIdSpecificEvalChange={
                            handleIdSpecificEvalChange
                          }
                          factName={factName}
                        />
                        <>
                          {spanEvalLoading ? (
                            <Skeleton
                              className={cn(
                                'animate-pulse-custom ml-1 w-16 rounded-full bg-[#8C9EA1]',
                                sizeStyles['compact']
                              )}
                            />
                          ) : (
                            <>
                              {(sequenceEvalArr(item)?.length ?? 0) > 0 ? (
                                <>
                                  <Separator
                                    orientation="vertical"
                                    className="mx-2 h-3 w-[1px] bg-line-tertiary"
                                  />
                                  {sequenceEvalArr(item)?.map((spanEval) => (
                                    <EvalBadge
                                      key={`${spanEval.fact_id}_${spanEval.eval_timestamp}`}
                                      size="compact"
                                      evalObj={spanEval}
                                      submitManualLabel={
                                        evaluateActions?.submitManualLabel
                                      }
                                      createEvalsJobs={
                                        evaluateActions?.createEvalsJobs
                                      }
                                      factName="inferences"
                                      startTime={
                                        item.inputData?.[0]?.startTime ??
                                        item.endTime
                                      }
                                      endTime={item.endTime}
                                      appName={
                                        appName ?? spanEvals?.app_name ?? ''
                                      }
                                      workflowName={
                                        spanEval.workflow_name ?? workflowName
                                      }
                                      templateEnums={getTemplateLabelEnums(
                                        evalTemplates,
                                        spanEval.eval_name
                                      )}
                                      sendEvalPromptToKahu={
                                        sendEvalPromptToKahu
                                      }
                                      filterStartTime={filterStartTime}
                                      filterEndTime={filterEndTime}
                                    />
                                  ))}
                                  <Separator
                                    className="mx-[6px] h-full min-h-3 border border-line-tertiary"
                                    orientation="vertical"
                                  />
                                </>
                              ) : null}
                              <span
                                className="cursor-pointer"
                                onClick={() => toggleCollapse(item.span_id)}
                              >
                                {collapsedIds.has(item.span_id)
                                  ? System.ChevronDownIcon(
                                      'text-[#8C9EA1] group-hover:text-white'
                                    )
                                  : System.ChevronUpIcon(
                                      'text-[#8C9EA1] group-hover:text-white'
                                    )}
                              </span>
                            </>
                          )}
                        </>
                      </div>
                    )
                  )}
                  {factName !== 'traces'
                    ? (item as TSequenceDataForConversations).spans_data.map(
                        (span) => (
                          <DetailSequenceView
                            key={`${item.trace_id}-${span.span_id}`}
                            data={{
                              ...span,
                              trace_id: item.trace_id,
                            }}
                            onSplit={onSplit}
                            totalTokens={span.model_attributes?.total_tokens}
                            completionTokens={
                              span.model_attributes?.completion_tokens
                            }
                            promptTokens={span.model_attributes?.prompt_tokens}
                            factName={factName}
                            appName={spanEvals?.app_name ?? ''}
                            workflowName={workflowName}
                            spanEvals={spanEvals}
                            filterStartTime={filterStartTime}
                            filterEndTime={filterEndTime}
                          />
                        )
                      )
                    : !collapsedIds.has((item as TSequenceData).span_id) && (
                        <DetailSequenceView
                          key={`${item.trace_id}-sequence`}
                          data={item as TSequenceData}
                          onSplit={onSplit}
                          totalTokens={
                            (item as TSequenceData).model_attributes
                              ?.total_tokens
                          }
                          completionTokens={
                            (item as TSequenceData).model_attributes
                              ?.completion_tokens
                          }
                          promptTokens={
                            (item as TSequenceData).model_attributes
                              ?.prompt_tokens
                          }
                          factName={factName}
                          searchTerm={deferredSearchTerm}
                          currentMatchIndex={currentMatchIndex}
                          globalMatchOffset={
                            (item as TSequenceDataGlobalMatchOffset)
                              .globalMatchOffset
                          }
                          navigableMatches={navigableMatches}
                          appName={spanEvals?.app_name ?? ''}
                          workflowName={workflowName}
                          spanEvals={spanEvals}
                          filterStartTime={filterStartTime}
                          filterEndTime={filterEndTime}
                        />
                      )}
                </React.Fragment>
              )
          )}

          {filterTraces(currentPagePromptsData?.sequenceData || []).length >
            maxTracesToShow && (
            <div className="my-3 flex flex-col items-center justify-center gap-3 py-2">
              <CustomButton
                customVariant="secondary"
                customSize="regular"
                onClick={handleShowMore}
                className={cn(
                  'rounded-sm border border-form-control-border-rest px-4 py-1 text-[#FFFFFF]',
                  showMoreInProgress && 'hidden'
                )}
              >
                {`${promptSequenceConstants?.General?.show} ${Math.min(filterTraces(currentPagePromptsData?.sequenceData || []).length - maxTracesToShow, CONVERSATION_COUNT_IN_ONE_TAB)} ${promptSequenceConstants?.General?.more}`}
              </CustomButton>

              <CustomButton
                customVariant="secondary"
                customSize="regular"
                onClick={handleShowMore}
                className={cn(
                  'flex items-center gap-2 rounded-sm border border-form-control-border-disabled px-4 py-1 text-[#FFFFFF] text-form-input-disabled transition-opacity',
                  !showMoreInProgress && 'hidden'
                )}
              >
                <span>{promptSequenceConstants?.General?.loading}</span>
                <BasicLoader className="size-4" />
              </CustomButton>
            </div>
          )}
        </ScrollArea>
      </div>

      {splitPayload && (
        <SplitPanel
          onSplit={onSplit}
          onCloseSplit={onCloseSplit}
          initialSearchTerm={searchInput}
          initialCurrentIndex={
            currentMatchIndex - splitPayload?.globalMatchOffset
          }
        />
      )}
    </div>
  );
};
